import * as React from 'react';
import { FC, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { AppointmentData, ClientOrder, ExtraData, ExtraItem, Payment, PaymentStatus } from '../../models';
import { useSelector } from 'react-redux';
import { getExtrasSettings, getTaxRates } from '../../store/settings/selectors';
import { getEntitiesDataItems } from '../../store/entities/selectors';
import { ClientEntity } from '../../store/entities/state';
import { createClientSearchConfig } from '../../shared/clientSearch';
import { AutocompleteTextField, NumberInputField, TextInputField } from '../../components/Forms/fields';
import { PaymentsList } from '../../components/Forms/fields/PaymentsList';
import { PaymentsButtons } from '../../components/Forms/fields/PaymentsButtons';
import ButtonsContainer from '../../components/ButtonsContainer';
import AppButton from '../../components/AppButton';
import { formatEuros } from '../../shared/string';
import { IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Typography from '../../components/Typography';
import { Guid } from '../../shared/utils';

export interface ClientOrderFormProps {
    defaultValue: ClientOrder;
    onSave: (order: ClientOrder) => void;
    onClose: () => void;
}

const buildEmptyItem = (): ExtraItem => ({
    id: Guid.newGuid(),
    name: '',
    value: 0,
    vat: 0,
    quantity: 1,
});

const SectionHeader = styled.div<{ children?: React.ReactNode }>`
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
`;

const ClientSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
    padding: var(--spacer-S) var(--spacer-M);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-left: var(--border-thick) solid var(--appBlue);
    border-radius: 8px;
    background: var(--bg-light);
`;

const ClientMeta = styled.div`
    font-size: 0.84rem;
    color: var(--text-muted);
`;

const TotalSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--spacer-XXS);
    padding-top: var(--spacer-S);
`;

const PaymentSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
`;


const ItemsList = styled.div<{ children?: React.ReactNode }>`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
`;

const ItemCard = styled.div<{ children?: React.ReactNode }>`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
    padding: var(--spacer-S) var(--spacer-M);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-left: var(--border-thick) solid var(--appBlue);
    border-radius: 8px;
    background: var(--bg-light);
`;

const ItemNameRow = styled.div<{ children?: React.ReactNode }>`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const ItemRow = styled.div<{ children?: React.ReactNode }>`
    display: flex;
    align-items: flex-end;
    gap: var(--spacer-XS);
`;

const ItemSeparator = styled.span`
    color: var(--text-muted);
    font-size: 0.85rem;
    flex-shrink: 0;
    padding-bottom: 2px;
`;

const ItemTotal = styled.div`
    flex: 1;
    text-align: right;
    font-weight: 700;
    color: var(--text-body);
    white-space: nowrap;
    font-size: 1rem;
`;

const getClients = getEntitiesDataItems<ClientEntity>('clients');

const ClientOrderForm: FC<ClientOrderFormProps> = ({ defaultValue, onSave, onClose }) => {
    const t = useTranslations();
    const availableExtras = useSelector(getExtrasSettings);
    const taxRates = useSelector(getTaxRates);
    const allClients = useSelector(getClients);

    const [order, setOrder] = useState<ClientOrder>(() => defaultValue);
    const [productSelectorKey, setProductSelectorKey] = useState(0);

    const emptyEntry = t(TK.emptyEntry);

    const clientSearchConfig = useMemo(() => createClientSearchConfig(allClients), [allClients]);

    const selectedClient = useMemo(
        () => allClients.find(c => c.id === order.clientId),
        [allClients, order.clientId]
    );

    const handleClientChanged = useCallback((option: string) => {
        const client = clientSearchConfig.getClientByOption(option);
        if (client) {
            setOrder(o => ({ ...o, clientId: client.id ?? '', clientName: client.name }));
        }
    }, [clientSearchConfig]);

    const sortedExtras = useMemo(
        () => [...availableExtras].sort((a, b) => a.name.localeCompare(b.name)),
        [availableExtras]
    );

    const productOptions = useMemo(
        () => [emptyEntry, ...sortedExtras.map(e => e.name)],
        [emptyEntry, sortedExtras]
    );

    const extraByName = useMemo(
        () => new Map<string, ExtraData>(sortedExtras.map(e => [e.name, e])),
        [sortedExtras]
    );

    const handleProductSelected = useCallback((name: string) => {
        if (!name) return;
        const newItem = name === emptyEntry
            ? buildEmptyItem()
            : (() => {
                const extra = extraByName.get(name);
                const taxRate = extra?.invoiceItemDetails?.taxId
                    ? taxRates?.find(r => r.id === extra.invoiceItemDetails!.taxId)
                    : undefined;
                return {
                    id: extra?.id ?? Guid.newGuid(),
                    name: extra?.name ?? name,
                    value: extra?.priceForClient ?? 0,
                    vat: taxRate ? taxRate.rate / 100 : 0,
                    quantity: 1,
                };
            })();
        setOrder(o => ({ ...o, items: [...o.items, newItem] }));
        setProductSelectorKey(k => k + 1);
    }, [emptyEntry, extraByName, taxRates]);

    const updateItem = useCallback((id: string, patch: Partial<ExtraItem>) => {
        setOrder(o => ({ ...o, items: o.items.map(i => i.id === id ? { ...i, ...patch } : i) }));
    }, []);

    const removeItem = useCallback((id: string) => {
        setOrder(o => ({ ...o, items: o.items.filter(i => i.id !== id) }));
    }, []);

    const totalWithoutVAT = useMemo(
        () => order.items.reduce((sum, i) => sum + i.value * i.quantity, 0),
        [order.items]
    );

    const totalWithVAT = useMemo(
        () => order.items.reduce((sum, i) => sum + i.value * i.quantity * (1 + i.vat), 0),
        [order.items]
    );

    const hasVAT = Math.abs(totalWithVAT - totalWithoutVAT) > 0.001;

    const totalPaid = useMemo(
        () => order.payments
            .filter(p => p.status === PaymentStatus.Manual || p.status === PaymentStatus.Success)
            .reduce((sum, p) => sum + (p.value ?? 0), 0),
        [order.payments]
    );

    const handlePaymentAdded = useCallback((payment: Payment) => {
        setOrder(o => ({ ...o, payments: [...(o.payments ?? []), payment] }));
    }, []);

    const handlePaymentsChanged = useCallback((payments: Payment[]) => {
        setOrder(o => ({ ...o, payments }));
    }, []);

    const handleSave = useCallback(() => {
        onSave({ ...order, missingToPay: totalWithVAT - totalPaid });
    }, [order, totalWithVAT, totalPaid, onSave]);

    // PaymentsButtons expects ClientPack | AppointmentData; ClientOrder has the same
    // shape for the properties it uses (id, payments) when showAutomatic/showPacks are false.
    const orderAsEntity = order as unknown as AppointmentData;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)', minWidth: 0 }}>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)' }}>
                <SectionHeader>{t(TK.client)}</SectionHeader>
                <ClientSection>
                    <AutocompleteTextField
                        id="order-client-selector"
                        value={clientSearchConfig.getOptionByClientId(order.clientId) ?? order.clientName ?? ''}
                        options={clientSearchConfig.options}
                        optionLabelResolver={clientSearchConfig.optionLabelResolver}
                        searchTextResolver={clientSearchConfig.searchTextResolver}
                        searchMatcher={clientSearchConfig.searchMatcher}
                        onValueChanged={handleClientChanged}
                    />
                    {selectedClient?.taxNumber && (
                        <ClientMeta>NIF: {selectedClient.taxNumber}</ClientMeta>
                    )}
                </ClientSection>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)' }}>
                <SectionHeader>{t(TK.items)}</SectionHeader>
                <AutocompleteTextField
                    key={productSelectorKey}
                    id="order-product-selector"
                    placeholder={t(TK.selectProduct)}
                    options={productOptions}
                    value=""
                    onValueChanged={handleProductSelected}
                    disableClearable
                />

                {order.items.length > 0 && (
                    <ItemsList>
                        {order.items.map(item => (
                            <ItemCard key={item.id}>
                                <ItemNameRow>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <TextInputField
                                            id={`order-item-${item.id}-name`}
                                            value={item.name}
                                            placeholder={t(TK.name)}
                                            onChange={name => updateItem(item.id, { name })}
                                        />
                                    </div>
                                    <IconButton size="small" onClick={() => removeItem(item.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </ItemNameRow>
                                <ItemRow>
                                    <div style={{ width: 64, flexShrink: 0 }}>
                                        <NumberInputField
                                            id={`order-item-${item.id}-qty`}
                                            label={t(TK.quantity)}
                                            value={item.quantity}
                                            min={1}
                                            onChange={qty => updateItem(item.id, { quantity: qty ?? 1 })}
                                        />
                                    </div>
                                    <ItemSeparator>×</ItemSeparator>
                                    <div style={{ width: 88, flexShrink: 0 }}>
                                        <NumberInputField
                                            id={`order-item-${item.id}-price`}
                                            label={t(TK.price)}
                                            value={item.value}
                                            min={0}
                                            append="€"
                                            onChange={value => updateItem(item.id, { value: value ?? 0 })}
                                        />
                                    </div>
                                    <div style={{ width: 88, flexShrink: 0 }}>
                                        <NumberInputField
                                            id={`order-item-${item.id}-vat`}
                                            label={`${t(TK.tax)} %`}
                                            value={Math.round(item.vat * 100)}
                                            min={0}
                                            max={100}
                                            append="%"
                                            onChange={pct => updateItem(item.id, { vat: (pct ?? 0) / 100 })}
                                        />
                                    </div>
                                    <ItemTotal>
                                        {formatEuros(item.value * item.quantity * (1 + item.vat))}
                                    </ItemTotal>
                                </ItemRow>
                            </ItemCard>
                        ))}
                    </ItemsList>
                )}

                {order.items.length > 0 && (
                    <TotalSection>
                        <Typography id="order-total-label" size="x-large" style={{ whiteSpace: 'nowrap' }}>
                            <b>{formatEuros(totalWithVAT)}</b>
                        </Typography>
                        {hasVAT && (
                            <Typography size="small" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                {t(TK.withoutVAT)}: {formatEuros(totalWithoutVAT)}
                            </Typography>
                        )}
                        {totalWithVAT - totalPaid > 0 && (
                            <span style={{ color: 'var(--danger-color)', fontSize: '0.875rem' }}>{t(TK.missingPayment)}</span>
                        )}
                    </TotalSection>
                )}
            </div>

            <PaymentSection>
                <SectionHeader>{t(TK.payments)}</SectionHeader>
                {order.payments.length > 0 && (
                    <PaymentsList
                        values={order.payments}
                        canDelete
                        onDelete={(_, idx) => setOrder(o => ({ ...o, payments: o.payments.filter((_, i) => i !== idx) }))}
                        missingToPay={totalWithVAT - totalPaid}
                    />
                )}
                <div style={{display: 'flex', flexDirection: 'row', gap: 'var(--spacer-XS)', flexWrap: 'wrap'}}>
                    <PaymentsButtons
                        entity={orderAsEntity}
                        totalToPay={totalWithVAT}
                        totalPaid={totalPaid}
                        showManual
                        onPaymentAdded={handlePaymentAdded}
                        onPaymentsChanged={handlePaymentsChanged}
                    />
                </div>
            </PaymentSection>

            <TextInputField
                id="order-notes"
                label={t(TK.notes)}
                type="textarea"
                rows={3}
                value={order.notes ?? ''}
                onChange={notes => setOrder(o => ({ ...o, notes }))}
            />

            <ButtonsContainer>
                <AppButton id="order-save-button" color="success" disabled={!order.items.length} onClick={handleSave}>
                    {t(TK.save)}
                </AppButton>
                <AppButton id="order-cancel-button" onClick={onClose}>
                    {t(TK.cancel)}
                </AppButton>
            </ButtonsContainer>
        </div>
    );
};

export default ClientOrderForm;
